import React, { useState, useEffect } from 'react';
import AlertModal from './AlertModal';

const TRACKS_URL = 'https://n293vk0zm6.execute-api.us-east-1.amazonaws.com/prod/tracks';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const num = typeof n === 'string' ? parseFloat(n.replace(/,/g, '')) : n;
  if (isNaN(num)) return n;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000)    return (num / 1000).toFixed(0) + 'K';
  return String(num);
}

function getEasternDate(offsetDays = 0) {
  const d       = new Date();
  const eastern = new Date(d.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
  eastern.setDate(eastern.getDate() - offsetDays);
  return eastern.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
}

function getRecentDates(n = 7) {
  const dates = [];
  for (let i = n - 1; i >= 0; i--) {
    dates.push(getEasternDate(i));
  }
  return dates;
}

function trackToAlert(t, date) {
  return {
    track_id:      t.track_id,
    name:          t.name,
    artist:        t.artist,
    detected_date: date,
    anomaly_score: t.flagged ? t.anomaly_score : null,
    rank:          t.rank,
    playcount:     fmt(t.playcount),
    listeners:     fmt(t.listeners),
    severity:      t.severity || null,
    both_models:   t.both_models || false,
    models_fired:  t.models_fired || '',
    cluster_id:    t.cluster_id,
    url:           t.url || 'https://last.fm',
    isChartTrack:  true,
    hasAnomaly:    t.flagged || false,
    ratio:         t.playcount_per_listener,
  };
}

export default function ChartHistory({ alerts = [], snapshotCount = 21 }) {
  const DATES = getRecentDates(snapshotCount);

  const [dayIdx, setDayIdx]               = useState(DATES.length - 1);
  const [tracks, setTracks]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [noData, setNoData]               = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);

  useEffect(() => {
    async function fetchTracks() {
      setLoading(true);
      setNoData(false);
      try {
        const dateStr = DATES[dayIdx];
        const res     = await fetch(`${TRACKS_URL}?date=${dateStr}`);
        const data    = await res.json();
        const items   = Array.isArray(data) ? data : (data.tracks || []);
        if (items.length > 0) {
          setTracks(items);
          setNoData(false);
        } else {
          setTracks([]);
          setNoData(true);
        }
      } catch {
        setTracks([]);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    }
    fetchTracks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayIdx]);

  const changeDay = (d) => {
    setDayIdx(prev => Math.max(0, Math.min(DATES.length - 1, prev + d)));
    setSelectedTrack(null);
  };

  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <div>
      {/* Date navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <button onClick={() => changeDay(-1)} disabled={dayIdx === 0} style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
          padding: '6px 12px', fontSize: '13px', color: 'var(--sub)',
          cursor: dayIdx === 0 ? 'not-allowed' : 'pointer',
          opacity: dayIdx === 0 ? 0.4 : 1
        }}>← Previous</button>

        <div style={{
          flex: 1, textAlign: 'center', background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: '8px',
          padding: '6px 12px', fontSize: '13px', fontWeight: 500, color: 'var(--text)'
        }}>
          {formatDateLabel(DATES[dayIdx])}
        </div>

        <button onClick={() => changeDay(1)} disabled={dayIdx === DATES.length - 1} style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
          padding: '6px 12px', fontSize: '13px', color: 'var(--sub)',
          cursor: dayIdx === DATES.length - 1 ? 'not-allowed' : 'pointer',
          opacity: dayIdx === DATES.length - 1 ? 0.4 : 1
        }}>Next →</button>
      </div>

      {/* Data update note */}
      <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted)', marginBottom: '14px' }}>
        Data updates daily at 8am ET
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)', fontSize: '13px' }}>
          Loading tracks...
        </div>
      )}

      {/* No data state */}
      {!loading && noData && (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: 'var(--muted)',
          fontSize: '13px', background: 'var(--surface)',
          borderRadius: '10px', border: '1px solid var(--border)'
        }}>
          No snapshot available for {formatDateLabel(DATES[dayIdx])}. Snapshots are saved daily at 8am ET.
        </div>
      )}

      {/* Track list */}
      {!loading && !noData && (
        <div>
          {tracks.map((t, idx) => {
            const isHigh      = t.severity === 'high';
            const borderColor = t.flagged ? (isHigh ? 'var(--accent)' : 'var(--amber)') : 'transparent';
            const hasPct      = t.flagged &&
                                t.playcount_pct_change !== null &&
                                t.playcount_pct_change !== undefined &&
                                t.playcount_pct_change !== '';
            const pctDisplay  = hasPct
              ? `+${(parseFloat(t.playcount_pct_change) * 100).toFixed(1)}% daily`
              : null;

            return (
              <div
                key={t.track_id || idx}
                className="chart-history-row"
                onClick={() => setSelectedTrack(trackToAlert(t, DATES[dayIdx]))}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px', background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${borderColor}`,
                  borderRadius: '8px', marginBottom: '6px',
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {/* Rank */}
                <div style={{
                  fontSize: '13px', fontWeight: 500, width: '28px',
                  textAlign: 'center', flexShrink: 0,
                  color: t.rank <= 10 ? 'var(--accent)' : 'var(--muted)'
                }}>
                  {t.rank}
                </div>

                {/* Track info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{t.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '1px' }}>{t.artist}</div>
                </div>

                {/* Metrics */}
                <div className="chart-history-metrics" style={{ display: 'flex', gap: '16px', flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>{fmt(t.playcount)}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Plays</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>{fmt(t.listeners)}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Listeners</div>
                  </div>
                </div>

                {/* Momentum pill — flagged tracks with pct_change only */}
                {pctDisplay && (
                  <div style={{
                    fontSize: '10px', color: 'var(--accent)',
                    padding: '2px 8px', borderRadius: '20px',
                    background: 'var(--accent-bg)', border: '1px solid var(--accent-bd)',
                    flexShrink: 0
                  }}>
                    {pctDisplay}
                  </div>
                )}

                {/* Anomaly badge */}
                {t.flagged && (
                  <div style={{
                    fontSize: '10px', background: 'var(--accent-bg)', color: 'var(--accent)',
                    padding: '2px 8px', borderRadius: '20px',
                    border: '1px solid var(--accent-bd)', flexShrink: 0
                  }}>
                    Anomaly
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertModal alert={selectedTrack} alerts={alerts} onClose={() => setSelectedTrack(null)} />
    </div>
  );
}